import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { APPOINTMENT_URL } from '../components/appointment.js';
import HeaderVideo from '../components/HeaderVideo';
import { workflows } from '../data/workflows.js';

const byId = Object.fromEntries(workflows.map((workflow) => [workflow.id, workflow]));
const JOURNEYS = [
  {
    workflow: byId.telemedicine,
    body: 'A shipboard officer records observations for an authorized shore team. Clinical advice and follow-up stay with the agreed service.',
    image: '/rebuild/images/bridge-officer.webp',
    alt: 'Illustration of a ship officer working on the bridge.',
    action: 'Explore care at sea',
  },
  {
    workflow: byId['peme-review'],
    body: 'A commissioned reviewer assesses completed medical documents, requirements and evidence before recording a disposition.',
    image: '/rebuild/images/peme-review.webp',
    alt: 'Illustration of a structured document review.',
    action: 'Explore document review',
  },
  {
    workflow: byId['peme-examination'],
    body: 'A clinic prepares the crew record, completes the in-person examination and attests the examiner certificate.',
    image: '/rebuild/images/peme-exam.webp',
    alt: 'Illustration of a seafarer at a medical examination.',
    action: 'Explore examinations',
  },
];

export default function Home() {
  return (
    <>
      <Helmet>
        <title>SeaScope | Maritime clinical workflows</title>
        <meta name="description" content="SeaScope connects ship-to-shore care, PEME document review and clinic examinations, with Crew Wellness as a separate continuity journey." />
        <link rel="canonical" href="https://seascope.tech/" />
        <link rel="preload" as="image" href="/rebuild/images/ship-aerial.webp" fetchPriority="high" />
      </Helmet>

      <header className="relative min-h-[640px] overflow-hidden py-24 lg:py-32">
        <HeaderVideo />
        <div className="pointer-events-none absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(255,255,255,.97) 0%, rgba(255,255,255,.93) 42%, rgba(255,255,255,.40) 70%, rgba(255,255,255,0) 100%)' }} />
        <div className="container-wide relative">
          <div className="max-w-[640px]">
            <span className="label-caps mb-6 block">SeaScope · Clinical services for fleets</span>
            <h1 className="mb-8 text-[36px] font-medium leading-[42px] lg:text-[56px] lg:leading-[64px]">
              Care at sea, medicals before sailing, health between
            </h1>
            <p className="mb-10 max-w-[540px] text-[17px] leading-7 text-ink-2 lg:text-[18px] lg:leading-[30px]">
              Three connected clinical jobs for maritime teams: respond to a case at sea, review
              completed medical documents, or complete a clinic examination. Each has its own
              people, evidence and decision.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <a href={APPOINTMENT_URL} target="_blank" rel="noopener" className="btn-primary">Book a meeting</a>
              <Link to="/crew-portal" className="btn-secondary">Crew portal information</Link>
            </div>
          </div>
        </div>
      </header>

      <section id="products" className="bg-bg-alt py-16 lg:py-24">
        <div className="container-wide">
          <span className="label-caps mb-4 block">Choose the work you need to do</span>
          <h2 className="mb-12 text-[28px] font-medium lg:text-[36px]">Three operational journeys</h2>
          <div className="grid gap-8 md:grid-cols-3">
            {JOURNEYS.map(({ workflow, body, image, alt, action }) => (
              <article key={workflow.id} className="overflow-hidden rounded-card border border-line bg-white">
                <img src={image} alt={alt} className="aspect-[4/3] w-full object-cover" width="800" height="600" loading="lazy" decoding="async" />
                <div className="p-8">
                  <h3 className="mb-4 text-2xl font-medium">{workflow.title}</h3>
                  <p className="mb-6 leading-7 text-ink-2">{body}</p>
                  <Link to={workflow.path} className="text-link font-medium">{action}</Link>
                </div>
              </article>
            ))}
          </div>
          <p className="mt-10 text-[15px] text-ink-2">
            Individual clinician? <Link to="/seascope-cds" className="text-link font-medium">Explore SeaScope CDS</Link>.
          </p>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24">
        <div className="container-wide grid items-center gap-12 lg:grid-cols-2">
          <div>
            <span className="label-caps mb-4 block">Continuity between medicals</span>
            <h2 className="mb-6 text-[28px] font-medium lg:text-[36px]">Crew Wellness is a separate journey</h2>
            <p className="mb-6 text-[17px] leading-7 text-ink-2">
              Seafarers can record readings between examinations in the Crew Wellness app.
              It is separate from Form A and does not create an examination result.
            </p>
            <p className="mb-7 leading-7 text-ink-2">
              Linking existing records for care requires an authorized team and a defined purpose.
              Access depends on the role and agreement, not a fleet-wide view of every medical detail.
              Read our <Link to="/privacy" className="text-link">privacy policy</Link>.
            </p>
            <Link to="/wellness" className="text-link font-medium">Explore Crew Wellness</Link>
          </div>
          <img src="/rebuild/images/crew-reading.webp" alt="Illustration of a seafarer recording a health reading." className="w-full rounded-card" width="1200" height="896" loading="lazy" decoding="async" />
        </div>
      </section>

      <section className="border-t border-line bg-bg-alt py-16 lg:py-24">
        <div className="container-wide max-w-3xl text-center">
          <h2 className="mb-5 text-[28px] font-medium lg:text-[36px]">Find the right next step</h2>
          <p className="mb-9 text-[17px] leading-7 text-ink-2">
            Fleet operators and clinics can discuss their contracted workflow. Seafarers can use
            the crew information hub to understand their personal medical link.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href={APPOINTMENT_URL} target="_blank" rel="noopener" className="btn-primary">Book a meeting</a>
            <Link to="/crew-portal" className="btn-secondary">Crew portal information</Link>
          </div>
        </div>
      </section>
    </>
  );
}
